import { useCallback, useEffect, useState } from 'react';
import ReactSelect, { GroupBase } from 'react-select';
import classNames from 'classnames';

import { InputLabel } from '@ui/InputLabel';
import { FormError } from '@ui/FormError';
import { MenuListPaginated } from '@ui/Select/SelectInternalComponents/MenuListPaginated';

import {
  InputActionMeta,
  SelectOption,
  SelectPaginatedProps,
} from '../../types';
import { getDefaultProps } from '../../utils';

import s from './SelectPaginated.sss';

export function SelectPaginated<
  Option extends SelectOption<unknown>,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  required, // We need required prop only for label
  options = [],
  perPage = 50,
  ...props
}: SelectPaginatedProps<Option, IsMulti, Group>) {
  const {
    className,
    label,
    error,
    helpText,
    inputClassName,
    onInputChange,
    getOptionLabel,
  } = props;

  const [page, setPage] = useState(1);
  const [paginatedOptions, setPaginatedOptions] = useState(options.slice(0, page * perPage));
  const [inputValue, setInputValue] = useState(props.inputValue);

  const defaultProps = getDefaultProps(props);

  const handleLoadMore = useCallback(() => (
    setPage((prevState) => prevState + 1)
  ), []);

  const handleInputChange = useCallback((newValue: string, actionMeta: InputActionMeta) => {
    setInputValue(newValue);

    if (onInputChange) {
      onInputChange(newValue, actionMeta);
    }
  }, [onInputChange]);

  const handleGetOptionLabel = useCallback((option: Option) => {
    return getOptionLabel ? getOptionLabel(option) : option.label;
  }, [getOptionLabel]);

  useEffect(() => {
    setPage(1);
  }, [options, inputValue]);

  useEffect(() => {
    setPaginatedOptions(
      options
        .filter((option) => {
          if (inputValue) {
            if (option && typeof option === 'object' && 'options' in option) {
              return option.options.find((groupOption) => {
                const optionLabel = handleGetOptionLabel(groupOption).toLowerCase();

                return optionLabel.includes(inputValue.trim().toLowerCase());
              });
            } else {
              const optionLabel = handleGetOptionLabel(option).toLowerCase();

              return optionLabel.includes(inputValue.trim().toLowerCase());
            }
          }

          return option;
        })
        .slice(0, page * perPage),
    );
  }, [options, handleGetOptionLabel, inputValue, page, perPage]);

  useEffect(() => {
    setInputValue(props.inputValue);
  }, [props.inputValue]);

  return (
    <div
      className={classNames(
        s.root,
        className,
      )}
    >
      {label
        && (
        <InputLabel
          className={s.label}
          required={required}
        >
          {label}
        </InputLabel>
        )}

      <ReactSelect
        {...defaultProps}
        {...props}
        options={paginatedOptions}
        onInputChange={handleInputChange}
        // @ts-ignore
        onLoadMore={handleLoadMore}
        // @ts-ignore
        components={{
          ...defaultProps.components,
          ...props.components,
          MenuList: MenuListPaginated,
        }}
        // @ts-ignore
        styles={{
          ...defaultProps.styles,
          ...props.styles,
          option: (base) => ({
            ...base,
            cursor: 'pointer',
            zIndex: 100,
            color: '#000',
            backgroundColor: '#fff',
            '&:hover': {
              backgroundColor: '#f7f7f7',
            },
          }),
        }}
        className={classNames(s.input, inputClassName)}
      />

      {helpText && <div className={s.helpText}>{helpText}</div>}
      {error && (
        Array.isArray(error)
          ? error.map((e, i) => <FormError key={i}>{e}</FormError>)
          : <FormError>{error}</FormError>
      )}
    </div>
  );
}
